/**
 * Splits a Results column template between the keys table and client-stitched column groups
 * (ADR 0020, DEC-ROLLOUT-001).
 *
 * The keys table computes what only the server can: which rows exist and their order. It emits
 * the symbol column and every column the current sort uses, because the server sorts only by
 * columns it computes and drops a sort on a column it was not sent. It also emits the columns a
 * client column reads (the News column reads the close change), because client columns resolve
 * their fields from the server table's columns. While a sticky search is active it also emits every
 * string column, visible or hidden: the table server matches the search text against the table's
 * string columns, so the keys table must carry the ones a full table would (OD-SEARCH-001). The
 * stage builders add validation and bespoke columns to it as before. Every other visible server
 * column is stitched.
 * The plan keeps market columns (bid, ask, last, OD-FEED-001) apart from regular ones, so a host
 * can serve them from a shared market provider; without one, the widget's consumer-local table
 * serves both (DEC-TABLES-001). Hidden columns request no data, and client columns are computed in
 * the browser, so neither belongs to any group.
 *
 * The keys columns depend only on column definitions, the sort, and whether a search is active,
 * never on visibility, so showing or hiding a column changes stitched demand without recreating the
 * keys table.
 */
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { FieldTypeCategory, isParameterTyped, simplifyType } from '@thinkalpha/language-services';
import type { SortModel } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import { ensureColumnBySlugWithName } from 'src/features/table/lib/ensureColumns';
import type { SlugMapDict } from 'src/lib/dictionary/dictionary';

/** The MVP market fields (OD-FEED-001). */
export type MarketField = 'bid' | 'ask' | 'last';

const MARKET_SLUGS: readonly (readonly [keyof SlugMapDict, MarketField])[] = [
    [Slugs.bid, 'bid'],
    [Slugs.ask, 'ask'],
    [Slugs.last, 'last'],
];

/** A visible market column and the feed field it shows. */
export interface MarketColumn {
    readonly field: MarketField;
    readonly column: TemplateColumn;
}

export interface StitchedColumnPlan {
    /** Columns the keys table computes, in template order; an added symbol column comes first. */
    readonly keysColumns: readonly TemplateColumn[];
    /** The column carrying each row's symbol; regular-column tables output it too. */
    readonly symbolColumn: TemplateColumn;
    /** Row field carrying the symbol: the symbol column's id. */
    readonly symbolFieldId: string;
    /** Visible market columns with their feed field. */
    readonly marketColumns: readonly MarketColumn[];
    /** Visible columns the consumer-local table serves; each binds its own id as field and column. */
    readonly regularColumns: readonly TemplateColumn[];
}

export interface StitchedColumnPlanOptions {
    /** Slugs a client column reads, by client key (`ClientColumnDefinition.requiredSlugs`). */
    readonly clientColumnSlugs?: (clientKey: string) => readonly string[];
    /** Whether a sticky search is active; the keys table then carries every string column. */
    readonly searching?: boolean;
}

/** Whether the table server matches search text against the column. */
function isStringColumn(column: TemplateColumn): boolean {
    const dataType = column.dataType;
    return dataType != null && !isParameterTyped(dataType) && simplifyType(dataType) === FieldTypeCategory.String;
}

/**
 * Plans one template under one sort. Returns null while the slug map has no usable symbol
 * indicator (it loads asynchronously): without one the keys table has no key column, so the
 * caller waits instead of creating a table.
 */
export function planStitchedColumns(
    columns: readonly TemplateColumn[],
    sort: SortModel | undefined,
    slugMap: SlugMapDict,
    { clientColumnSlugs = () => [], searching = false }: StitchedColumnPlanOptions = {},
): StitchedColumnPlan | null {
    // The slug map is typed complete but fills in after load.
    const symbolIndicator: SlugMapDict[typeof Slugs.symbol] | undefined = slugMap[Slugs.symbol];
    const templateSymbol = columns.find(
        (column) => column.type === 'indicator' && column.indicator.id === symbolIndicator?.id,
    );
    // A template without a symbol column gets a hidden one, as other symbol-keyed widgets do.
    const symbolColumn =
        templateSymbol ?? ensureColumnBySlugWithName(slugMap, [], { name: 'Symbol', slug: Slugs.symbol })[0];
    if (symbolColumn === undefined) {
        return null;
    }
    const sortedIds = new Set(Object.keys(sort ?? {}));
    // Indicators that client columns in this template read, resolved through the slug map.
    const clientReadIndicatorIds = new Set<string>();
    for (const column of columns) {
        if (column.type === 'client') {
            for (const slug of clientColumnSlugs(column.clientKey)) {
                const indicator: SlugMapDict[keyof SlugMapDict] | undefined = slugMap[slug as keyof SlugMapDict];
                if (indicator !== undefined) {
                    clientReadIndicatorIds.add(indicator.id);
                }
            }
        }
    }
    const clientReads = (column: TemplateColumn) =>
        column.type === 'indicator' && clientReadIndicatorIds.has(column.indicator.id);
    const marketFieldOf = (column: TemplateColumn): MarketField | undefined => {
        if (column.type !== 'indicator') {
            return undefined;
        }
        for (const [slug, field] of MARKET_SLUGS) {
            const indicator: SlugMapDict[typeof slug] | undefined = slugMap[slug];
            if (indicator?.id === column.indicator.id) {
                return field;
            }
        }
        return undefined;
    };

    const keysColumns: TemplateColumn[] = [];
    const marketColumns: MarketColumn[] = [];
    const regularColumns: TemplateColumn[] = [];
    for (const column of columns) {
        if (column.type === 'client') {
            continue;
        }
        if (
            column === symbolColumn ||
            sortedIds.has(column.id) ||
            clientReads(column) ||
            (searching && isStringColumn(column))
        ) {
            keysColumns.push(column);
            continue;
        }
        if (!column.visible) {
            continue;
        }
        const marketField = marketFieldOf(column);
        if (marketField === undefined) {
            regularColumns.push(column);
        } else {
            marketColumns.push({ field: marketField, column });
        }
    }

    return {
        keysColumns: templateSymbol === undefined ? [symbolColumn, ...keysColumns] : keysColumns,
        symbolColumn,
        symbolFieldId: symbolColumn.id,
        marketColumns,
        regularColumns,
    };
}
